import { Component, inject, ViewChild } from '@angular/core';
import { CredentialType } from 'enums/CredentialType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProviderFactory } from 'services/company-data-provider';
import { ComponentUrl, NavigationService } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';
import { ListView } from 'modules/list-view';
import { ProductPlanComponent } from './product-plan.component';
import { Pipes } from 'modules/pipes';
import { CredentialRequired } from 'services/credential.service';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { Inputs } from 'modules/inputs';
import { DataTracking } from 'services/data-tracking';
import { TagsComponent } from 'components/input/tags/tags.component';
import { TagModel } from 'models/generated/Models.TagModel';
import { ButtonComponent } from 'components/input/button/button.component';
import { DateService } from 'services/date.service';

@Component({
	selector: 'search-product-plans',
	templateUrl: './search-product-plans.component.html',
	imports: [Layout, ListView, Pipes, Inputs, TagsComponent],
	providers: [DataTracking]
})
@ComponentUrl('search-product-plans')
@CredentialRequired(CredentialType.SearchProductPlans)
export class SearchProductPlansComponent {

	public renewalDateFrom: Date = new Date();
	public renewalDateTo: Date = inject(DateService).addMonths(new Date(), 1);
	public selectedProductPlan?: ProductPlanModel;
	public productPlans?: ProductPlanModel[];
	public tags: TagModel[] = [];
	public tagsOnProductPlans?: TagModel[];
	public onlyDeliveredPlans = true;
	public onlyTerminatedPlans = true;

	private productPlanService = inject(ProductPlanService);
	private navigation = inject(NavigationService);
	private trackingContext = inject(DataTracking);

	@ViewChild('searchButton') private searchButton?: ButtonComponent;

	constructor() {
		const storedTags = localStorage.getItem('search-product-plans.tags');
		if (storedTags) this.tags = (JSON.parse(storedTags) as string[]).map(t => ({ tag: t } as TagModel));
		this.searchProductPlans();
		this.productPlanService.getAllProductPlanTags().then(tags => this.tagsOnProductPlans = tags.map(t => ({ tag: t } as TagModel)));
	}

	async selectProductPlan(plan: ProductPlanModel) {
		if (!plan.id || !plan.companyId) return;
		this.selectedProductPlan = plan;
		const view = await this.navigation.openRelativeView(this, 'product-plan', CompanyProductPlanComponent);
		view.loadProductPlan(plan.id, plan.companyId);
	}

	async searchProductPlans() {
		localStorage.setItem('search-product-plans.tags', JSON.stringify(this.tags.map(t => t.tag)));
		this.productPlans = undefined;
		this.productPlans = await this.productPlanService.searchProductPlans({
			expiresFrom: this.renewalDateFrom,
			expiresTo: this.renewalDateTo,
			tags: this.tags.map(t => t.tag),
			isDelivered: this.onlyDeliveredPlans ? true : undefined,
			isTerminated: this.onlyTerminatedPlans ? true : undefined,
			includeCompanyData: true,
		}, this.trackingContext);
		this.searchButton?.reset();
	}
}

@Component({
	template:
		'@if (productPlan && accountingPlan) { <product-plan [productPlan]="productPlan" [accountingPlan]="accountingPlan" /> }' +
		'@else { <panel> <skeleton /></panel> }' +
		'@if (company) { <panel><company-information [company]="company" /></panel> }',
	imports: [ProductPlanComponent, CompanyInformationComponent, Layout],
	providers: [CompanyDataProviderFactory]
})
export class CompanyProductPlanComponent {

	private dataProviderFactory = inject(CompanyDataProviderFactory);
	private accountingService = inject(AccountingService);

	public async loadProductPlan(productPlanId: string, companyId: string) {
		const provider = this.dataProviderFactory.getProviderForCompanyId(companyId);
		this.productPlan = await provider.getProductPlanWithAccountingPlans(productPlanId);
		this.accountingPlan = this.accountingService.filterCurrentAccountingPlan(this.productPlan.accountingPlans);
		this.company = await provider.getCompany();
	}

	productPlan?: ProductPlanModel;
	accountingPlan?: AccountingPlanModel;
	company?: CompanyModel;
}
